import css from 'classnames';
import React, { useEffect, useRef, useState } from 'react';

import { DiceReceiverManager, DiceReceiverManagerEvents } from '../services';
import type { Settings } from '../types';

const ReadyToCastWwe = () => {
    return (
        <div className="ready-to-cast wwe-ready-to-cast loaded">
            <img
                src="https://doris-static-assets-80d63b5e.s3-eu-west-1.amazonaws.com/wwe/wwe-logo.svg"
                className="wwe-ready-to-cast__logo"
                width={400}
                height={400}
            />
            <div className="wwe-ready-to-cast__title">Ready to cast from your device</div>
        </div>
    );
};

const ReadyToCastGeneric = () => {
    const [backgroundUrl, setBackgroundUrl] = useState<string>();
    const [logoUrl, setLogoUrl] = useState<string>();
    const [isBackgroundLoaded, setIsBackgroundLoaded] = useState(false);
    const [isLogoLoaded, setIsLogoLoaded] = useState(false);
    const backgroundUrlRef = useRef<string | null>(null);
    const logoUrlRef = useRef<string | null>(null);

    useEffect(() => {
        const diceReceiverManager = DiceReceiverManager.getInstance();
        const applySettings = (settings?: Settings) => {
            const backgroundImageUrl = settings?.backgroundImageUrl;
            const logoImageUrl = settings?.logoImageUrl;
            const nextBackgroundRef = backgroundImageUrl ?? null;
            const nextLogoRef = logoImageUrl ?? null;
            const hasBackgroundChanged = backgroundUrlRef.current !== nextBackgroundRef;
            const hasLogoChanged = logoUrlRef.current !== nextLogoRef;

            if (hasBackgroundChanged || !backgroundImageUrl) {
                backgroundUrlRef.current = nextBackgroundRef;
                setIsBackgroundLoaded(!backgroundImageUrl);
            }

            if (hasLogoChanged || !logoImageUrl) {
                logoUrlRef.current = nextLogoRef;
                setIsLogoLoaded(!logoImageUrl);
            }

            setBackgroundUrl(backgroundImageUrl);
            setLogoUrl(logoImageUrl);
        };

        applySettings(diceReceiverManager.getSettings());

        const handleSettingsChanged = (settings: Settings) => {
            applySettings(settings);
        };

        diceReceiverManager.on(DiceReceiverManagerEvents.SETTINGS_CHANGED, handleSettingsChanged);

        return () => {
            diceReceiverManager.off(DiceReceiverManagerEvents.SETTINGS_CHANGED, handleSettingsChanged);
        };
    }, []);

    const isReady = isBackgroundLoaded && isLogoLoaded;

    const className = css('ready-to-cast', isReady && 'loaded');

    return (
        <div className={className} aria-hidden={!isReady}>
            {backgroundUrl ? (
                <img
                    className="background-image"
                    src={backgroundUrl}
                    alt=""
                    onLoad={() => setIsBackgroundLoaded(true)}
                    onError={() => setIsBackgroundLoaded(true)}
                />
            ) : null}
            {logoUrl ? (
                <img
                    className="logo"
                    src={logoUrl}
                    alt="Ready to cast logo"
                    onLoad={() => setIsLogoLoaded(true)}
                    onError={() => setIsLogoLoaded(true)}
                />
            ) : null}
        </div>
    );
};

const getIsWwe = () => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const realm = urlSearchParams.get('realm');
    return realm === 'dce.wwe';
};

export const ReadyToCast: React.FC = () => {
    const [isWwe] = useState(getIsWwe());
    return isWwe ? <ReadyToCastWwe /> : <ReadyToCastGeneric />;
};
